import type { GetResult } from "@/api/types";
import {
  distanceMeters,
  findUnambiguousNearestLocation,
} from "@/modules/anonymousBrowsing/findUnambiguousNearestLocation";
import type { GuestPosition } from "@/modules/anonymousBrowsing/requestGuestPosition";

type LocationCoordinates = GetResult<"/multi-platform/platform/{platformId}/location-coordinates">;

const METERS_PER_MILE = 1609.344;
const METERS_PER_KILOMETER = 1000;
/** How close the nearest branch must be for the patient to count as being in its city. */
const CITY_SUGGESTION_RADIUS_METERS = 15 * METERS_PER_MILE;

export type SignUpLocationSuggestion = {
  locationId: string;
  /**
   * `location`: the patient is unambiguously at this branch (200 m rule), so preselect it.
   * `city`: the branch is the nearest within 15 miles, so preselect only its city.
   */
  scope: "location" | "city";
};

const findNearestGeocodedLocation = (
  locations: LocationCoordinates["locations"],
  position: GuestPosition,
) => {
  let nearest: { locationId: string; meters: number } | null = null;
  for (const { locationId, latitude, longitude } of locations) {
    if (latitude === null || longitude === null) continue;
    const meters = distanceMeters(position, latitude, longitude);
    if (nearest === null || meters < nearest.meters) nearest = { locationId, meters };
  }
  return nearest;
};

/**
 * The browser position decides when the patient shares it; the IP-derived nearest branch is only
 * consulted when they don't, since a precise position that is far from every branch outranks it.
 */
export const suggestSignUpLocation = (
  coordinates: LocationCoordinates,
  position: GuestPosition | null,
): SignUpLocationSuggestion | null => {
  if (position) {
    const preciseLocationId = findUnambiguousNearestLocation(coordinates.locations, position);
    if (preciseLocationId) return { locationId: preciseLocationId, scope: "location" };

    const nearest = findNearestGeocodedLocation(coordinates.locations, position);
    return nearest && nearest.meters <= CITY_SUGGESTION_RADIUS_METERS
      ? { locationId: nearest.locationId, scope: "city" }
      : null;
  }

  const { approximateNearestLocationId, approximateNearestLocationDistanceInKilometers } =
    coordinates;
  if (
    approximateNearestLocationId === null ||
    approximateNearestLocationDistanceInKilometers === null
  ) {
    return null;
  }
  return approximateNearestLocationDistanceInKilometers * METERS_PER_KILOMETER <=
    CITY_SUGGESTION_RADIUS_METERS
    ? { locationId: approximateNearestLocationId, scope: "city" }
    : null;
};
